/**
 * The order's Details (the twistie under its headline, Ben 3 Oct) and the
 * purchase order's Details tab, drawn as the estimate's Details is (#2305):
 * two columns of label and value, empty values left out — on the purchase
 * order in one titled card. On the left, what it is and where it came from;
 * on the right, who pays or supplies it. The header keeps only the facts you
 * scan for; everything else is here.
 *
 * Every sentence is the backend's (who placed it, the bill-to line, how the
 * payer pays, how and by whom a purchase order was raised and placed).
 */
import { Fragment, type ReactNode } from "react";
import type {
  OrderView,
  PurchaseOrderView,
} from "@shared-core/data/order-to-invoice-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { deliveryDestinationLabel } from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import { ObjectChip } from "../../patterns/ObjectChip";
import { TabCard } from "./OrderPanels";
import { ProjectChip } from "./ProjectChip";

interface KeyValue {
  label: string;
  value: ReactNode;
}

type Rows = Array<KeyValue | null | false>;

/** Label beside value, one per row; a row with nothing to say is not drawn. */
function KeyValues({ rows }: { rows: Rows }) {
  const shown = rows.filter((r): r is KeyValue => !!r && r.value != null);
  if (shown.length === 0) return null;
  return (
    <dl className="grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-sm">
      {shown.map((row) => (
        <Fragment key={row.label}>
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 break-words font-medium">{row.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** Two columns side by side on a wide screen. */
function DetailsColumns({
  label,
  left,
  right,
}: {
  label: string;
  left: Rows;
  right: Rows;
}) {
  return (
    <section
      aria-label={label}
      className="grid items-start gap-x-10 gap-y-4 lg:grid-cols-2"
    >
      <KeyValues rows={left} />
      <KeyValues rows={right} />
    </section>
  );
}

/** The titled card a Details tab is. */
function DetailsCard({
  title,
  ...columns
}: {
  label: string;
  title: string;
  left: Rows;
  right: Rows;
}) {
  return (
    <TabCard>
      {/* The estimate's Details heading (#2305): the same size on every page. */}
      <h2 className="text-sm font-semibold">{title}</h2>
      <DetailsColumns {...columns} />
    </TabCard>
  );
}

function Chips({ children }: { children: ReactNode }) {
  return <span className="flex flex-wrap items-center gap-2">{children}</span>;
}

/** The order's Details, under its headline: no card of its own, it is in the order's. */
export function OrderDetailsPanel({
  order,
  onSetContact,
}: {
  order: OrderView;
  /** Open Set contact; offered when the order carries its URL. */
  onSetContact?: () => void;
}) {
  const purchaseOrders = order.lines.flatMap((line) =>
    line.production?.purchase_order_no
      ? [
          {
            lineNo: line.line_no,
            no: line.production.purchase_order_no,
            supplier:
              line.production.supplier_name ?? line.production.supplier_no,
          },
        ]
      : [],
  );
  return (
    <DetailsColumns
      label={`Details of order ${order.order_no}`}
      left={[
        { label: "Order number", value: order.order_no },
        { label: "Title", value: order.title },
        order.project !== null && {
          label: "Project",
          value: (
            <ProjectChip
              project={order.project}
              currentOrderNo={order.order_no}
            />
          ),
        },
        { label: "Placed", value: order.placed_label },
        { label: "Channel", value: order.channel_label },
        order.quote_no !== null && {
          label: "Quote",
          value: (
            <ObjectChip
              label={order.quote_no}
              to={paths.quoteDetail({ quoteNo: order.quote_no })}
            />
          ),
        },
        order.customer_order_number_label !== null && {
          label: "Customer PO",
          value: order.customer_order_number_label,
        },
        order.required_on !== null && {
          label: "Needed by",
          value: formatDateOrDash(order.required_on),
        },
        purchaseOrders.length > 0 && {
          label:
            purchaseOrders.length === 1 ? "Purchase order" : "Purchase orders",
          value: (
            <Chips>
              {purchaseOrders.map((p) => (
                <ObjectChip
                  key={`${p.no}-${p.lineNo}`}
                  label={p.no}
                  detail={[p.lineNo, p.supplier].filter(Boolean).join(" · ")}
                  to={paths.purchaseOrderDetail({ purchaseOrderNo: p.no })}
                />
              ))}
            </Chips>
          ),
        },
      ]}
      right={[
        { label: "Bill to", value: order.bill_to.label },
        order.payment !== null
          ? { label: "Payment", value: order.payment.method_label }
          : order.bill_to.payment_sentence !== null && {
              label: "Payment",
              value: order.bill_to.payment_sentence,
            },
        {
          label: "Contact",
          value: (
            <span className="inline-flex flex-wrap items-baseline gap-x-2">
              <span>{order.contact_label}</span>
              {order.set_contact_url && onSetContact && (
                <button
                  type="button"
                  onClick={onSetContact}
                  className="text-sm font-normal underline underline-offset-2"
                >
                  {order.contact ? "Change…" : "Set contact…"}
                </button>
              )}
            </span>
          ),
        },
      ]}
    />
  );
}

/** The supplier, one fact per line: its name, its CIS number, and Tharstern's code when Tharstern knows it. */
function SupplierBlock({ po }: { po: PurchaseOrderView }) {
  const s = po.supplier;
  return (
    <span className="flex flex-col">
      <span>{s.name ?? s.supplier_no}</span>
      {s.name && s.supplier_no && (
        <span className="font-normal tabular-nums text-muted-foreground">
          {s.supplier_no}
        </span>
      )}
      {s.supplier_code && (
        <span className="font-normal text-muted-foreground">
          Tharstern {s.supplier_code}
        </span>
      )}
    </span>
  );
}

/** The purchase order's Details tab. */
export function PurchaseOrderDetailsPanel({ po }: { po: PurchaseOrderView }) {
  const destinations = [
    ...new Set(
      po.lines.flatMap((l) =>
        l.delivery_lines.map((d) => deliveryDestinationLabel(d.destination)),
      ),
    ),
  ];
  return (
    <DetailsCard
      label={`Details of purchase order ${po.purchase_order_no}`}
      title="Purchase order details"
      left={[
        { label: "PO number", value: po.purchase_order_no },
        { label: "Supplier", value: <SupplierBlock po={po} /> },
        po.route_label !== null && { label: "Route", value: po.route_label },
        { label: "Placed", value: po.placed_via_label },
        po.supplier_reference !== null && {
          label: "Supplier's reference",
          value: po.supplier_reference,
        },
        po.raised_label !== null && { label: "Raised", value: po.raised_label },
        po.cancellation_reason !== null && {
          label: "Cancelled",
          value: po.cancellation_reason,
        },
      ]}
      right={[
        destinations.length > 0 && {
          label: "Delivers to",
          value: (
            <ul className="space-y-0.5">
              {destinations.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          ),
        },
      ]}
    />
  );
}
